import React, { useState, useEffect } from "react";
import "./index.css";
import "@fontsource/roboto/300.css";
import "@fontsource/roboto/400.css";
import "@fontsource/roboto/500.css";
import "@fontsource/roboto/700.css";
import "@fontsource/jetbrains-mono/400.css";
import "@fontsource/jetbrains-mono/500.css";
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { CardMedia, useTheme } from "@mui/material";
import { doc, getDoc } from "firebase/firestore";
import { auth, db } from "./firebase";
import Login from "./components/login";
import Home from "./components/home";
import Profile from "./components/profile";
import Propuestas from "./components/propuestas";
import Proyectos from "./components/proyectos";
import Notifications from "./components/notifications";
import Publish from "./components/publish";
import PublishProposal from "./components/publishProposal";
import Edit from "./components/edit";
import ChatBox from "./components/chatbox";
import Chats from "./components/chats";
import Header3 from "./components/Header3";
import Project from "./components/project";
import Members from "./components/members";
import Atras from "./components/atras";
import BottomNav from "./components/BottomNav";
import Terms from "./components/terms";
import ProjectProposals from './components/projectProposals';
import Privacidad from "./components/privacyPolicy";
import ViewProfile from "./components/viewProfile";
import DirectChat from "./components/directChat";
import AccountSuspended from "./components/accountSuspended";
import SkillTest from "./components/skillTest";
import Payments from "./components/payments";
import ChangePassword from "./components/changePassword";
import Activity from "./components/activity";
import DeleteAccount from "./components/deleteAccount";
import HelpSupport from "./components/helpSupport";
import NotificationPreferences from "./components/notificationPreferences";
import { isLoggedIn, endSession } from "./session";
import logo from "./imgs/logo_logo.png";
import Subscription from "./components/subscription";
import { SubscriptionProvider, useSubscriptionContext } from './components/SubscriptionContext';
import { ThemeModeProvider } from "./components/ThemeModeContext";
import Layout from "./components/Layout";
import Workspace from "./components/Workspace";

const Loading = () => {
  const theme = useTheme();
  return (
    <div
      className="loading-container"
      style={{
        backgroundColor: theme.palette.background.paper,
        boxShadow: theme.shadows[3],
      }}
    >
      <CardMedia
        className="loading-img"
        alt="Cargando..."
        component="img"
        image={logo}
      />
    </div>
  );
};

// Componente para proteger rutas que requieren autenticación
const ProtectedRoute = ({ children }) => {
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [accountStatus, setAccountStatus] = useState(null);
  const location = useLocation();

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const user = await isLoggedIn();
        setIsAuthenticated(!!user);
        if (user) {
          const userDoc = await getDoc(doc(db, "users", user.uid));
          if (userDoc.exists()) {
            setAccountStatus(userDoc.data().accountStatus || null);
          }
        }
      } catch (error) {
        console.error("Error checking auth:", error);
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, []);

  if (loading) {
    return <Loading />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (accountStatus === "suspended" || accountStatus === "pending_deletion") {
    return <AccountSuspended status={accountStatus} />;
  }

  return children;
};

const ProtectedPublish = ({ children }) => {
  const { subscription } = useSubscriptionContext();
  const isSubscriptionActive = subscription?.status === 'ACTIVE';
  const location = useLocation(); // Get current location

  // Check authentication FIRST
  const isLoggedIn = !!auth.currentUser; // Directly check if there's a current user

  if (!isLoggedIn) {
    return <Navigate to="/login" state={{ from: location }} replace />; // Redirect to login with location state
  }

  // THEN, if authenticated, check subscription
  if (!isSubscriptionActive) {
    return <Navigate to="/subscription" state={{ from: location }} replace />; // Redirect to subscription with location state
  }

  return children;
};

const AppRoutes = ({ user, handleLogout }) => {
  return (
    <Routes>
      {/* Rutas públicas */}
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Login />} />
      <Route path="/forgotPassword" element={<Login />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/privacyPolicy" element={<Privacidad />} />

      {/* Ruta raíz */}
      <Route
        path="/"
        element={
          user ? (
            <Home handleLogout={handleLogout} />
          ) : (
            <Navigate to="/login" replace />
          )
        }
      />

      {/* Rutas protegidas */}
      <Route
        path="/home"
        element={
          <ProtectedRoute>
            <Home handleLogout={handleLogout} />
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <Profile />
          </ProtectedRoute>
        }
      />
      <Route
        path="/notifications"
        element={
          <ProtectedRoute>
            <Notifications />
          </ProtectedRoute>
        }
      />
      <Route
        path="/propuestas"
        element={
          <ProtectedRoute>
            <Propuestas />
          </ProtectedRoute>
        }
      />
      <Route
        path="/chats"
        element={
          <ProtectedRoute>
            <Chats />
          </ProtectedRoute>
        }
      />
      <Route
        path="/chatbox/:projectId/:proposalId/:freelancerUid/:authUser"
        element={
          <ProtectedRoute>
            <ChatBox />
          </ProtectedRoute>
        }
      />
      <Route
        path="/project/:projectId/proposals/:proposalId"
        element={
          <ProtectedRoute>
            <ProjectProposals />
          </ProtectedRoute>
        }
      />
      <Route
        path="/project/:projectId"
        element={
          <ProtectedRoute>
            <Project />
          </ProtectedRoute>
        }
      />
      <Route
        path="/publish"
        element={
          <ProtectedRoute>
            <Publish />
          </ProtectedRoute>
        }
      />
      <Route
          path="/publishProposal"
          element={
            <ProtectedRoute>
              <ProtectedPublish>
                <PublishProposal />
              </ProtectedPublish>
            </ProtectedRoute>
          }
        />
      <Route
        path="/header3/:projectId"
        element={
          <ProtectedRoute>
            <Header3 />
          </ProtectedRoute>
        }
      />
      <Route
        path="/atras"
        element={
          <ProtectedRoute>
            <Atras />
          </ProtectedRoute>
        }
      />
      <Route
        path="/bottomNav"
        element={
          <ProtectedRoute>
            <BottomNav />
          </ProtectedRoute>
        }
      />
      <Route
        path="/members"
        element={
          <ProtectedRoute>
            <Members />
          </ProtectedRoute>
        }
      />
      <Route
        path="/proyectos"
        element={
          <ProtectedRoute>
            <Proyectos />
          </ProtectedRoute>
        }
      />
      <Route
        path="/edit"
        element={
          <ProtectedRoute>
            <Edit />
          </ProtectedRoute>
        }
      />
      <Route
        path="/subscription"
        element={
          <ProtectedRoute>
            <Subscription />
          </ProtectedRoute>
        }
      />
      <Route
        path="/freelancer/:uid"
        element={
          <ProtectedRoute>
            <ViewProfile />
          </ProtectedRoute>
        }
      />
      <Route
        path="/messages/:uid"
        element={
          <ProtectedRoute>
            <DirectChat />
          </ProtectedRoute>
        }
      />
      <Route
        path="/skillTest"
        element={
          <ProtectedRoute>
            <SkillTest />
          </ProtectedRoute>
        }
      />
      <Route
        path="/payments"
        element={
          <ProtectedRoute>
            <Payments />
          </ProtectedRoute>
        }
      />
      <Route
        path="/changePassword"
        element={
          <ProtectedRoute>
            <ChangePassword />
          </ProtectedRoute>
        }
      />
      <Route
        path="/activity"
        element={
          <ProtectedRoute>
            <Activity />
          </ProtectedRoute>
        }
      />
      <Route
        path="/deleteAccount"
        element={
          <ProtectedRoute>
            <DeleteAccount />
          </ProtectedRoute>
        }
      />
      <Route
        path="/help"
        element={
          <ProtectedRoute>
            <HelpSupport />
          </ProtectedRoute>
        }
      />
      <Route
        path="/notificationPreferences"
        element={
          <ProtectedRoute>
            <NotificationPreferences />
          </ProtectedRoute>
        }
      />
      <Route
        path="/workspace"
        element={
          <ProtectedRoute>
            <Workspace />
          </ProtectedRoute>
        }
      />

      {/* Ruta para manejar URLs no encontradas */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

const App = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const user = await isLoggedIn();
        setUser(user);
      } catch (error) {
        console.error("Error al verificar la autenticación:", error);
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, []);

  const handleLogout = async () => {
    try {
      await endSession();
      setUser(null);
    } catch (error) {
      console.error("Error al cerrar sesión:", error);
    }
  };

  return (
    <ThemeModeProvider>
      {loading ? (
        <Loading />
      ) : (
        <Router>
          <SubscriptionProvider>
            <Layout>
              <AppRoutes user={user} handleLogout={handleLogout} />
            </Layout>
          </SubscriptionProvider>
        </Router>
      )}
    </ThemeModeProvider>
  );
};

export default App;